export function DebounceAsync(ms: number = 300) {
  const timers = new WeakMap<object, Map<string | symbol, any>>();

  return function(
    _target: any,
    propertyKey: string | symbol,
    descriptor: PropertyDescriptor,
  ) {
    const original = descriptor.value;
    if (typeof original !== 'function') {
      throw new TypeError('DebounceAsync can only be applied to methods');
    }

    descriptor.value = function (this: PropertyDescriptor, ...args: any[]) {
      let instanceMap = timers.get(this);
      if (!instanceMap) {
        instanceMap = new Map<string | symbol, any>();
        timers.set(this, instanceMap);
      }

      // existing entry shape: { timer: any, pending: Array<{resolve, reject, args}> }
      let entry = instanceMap.get(propertyKey);
      if (!entry) {
        entry = {
          timer: null as any,
          pending: [] as Array<any>,
        };
        instanceMap.set(propertyKey, entry);
      }

      const handler = async () => {
        entry.timer = null;
        const pending = entry.pending.slice();
        entry.pending = [];

        try {
          const result = await Promise.resolve(
            original.apply(this, entry.lastArgs));
          for (const p of pending) p.resolve(result);
        } catch (err) {
          for (const p of pending) p.reject(err);
        }
      };

      const debounce = (
        resolve: (value: unknown) => void,
        reject: (reason?: any) => void,
      ) => {
        // push this call's resolvers and args (we only keep last args for execution)
        entry.pending.push({
          resolve,
          reject,
        });
        entry.lastArgs = args;

        if (entry.timer) {
          clearTimeout(entry.timer);
        }

        entry.timer = setTimeout(handler, ms);
      };

      return new Promise(debounce);
    };

    return descriptor;
  };
}
